import {
  DEFAULT_ACTIVITY_TIMEZONE,
  formatIsoWithLocalOffset,
  formatWallClockHm,
  localDateKeyFromDate,
  localDateTimeFromParts,
  wallClockInTimeZone,
} from '../../utils/activityDateTime';
import type { PlantafelDayHours, PlantafelVisibleWeekdays } from '../../utils/plantafelSettings';
import {
  clipBlockHoursToViewport,
  DEFAULT_PLANTAFEL_DISPLAY_SETTINGS,
  plantafelHourSpan,
} from '../../utils/plantafelSettings';
import {
  clipSpanToVisibleColumns,
  visibleColumnCount,
  visibleColumnToAbsoluteDay,
  weekDayWidthPct,
} from '../../utils/plantafelVisibleDays';
import type { GridBlock } from './plantafelTypes';
import type { PlantafelViewMode } from './plantafelTypes';
import { addDays } from './plantafelViewport';

export const SNAP_HOURS = 0.25;

export function formatHourFraction(h: number): string {
  if (h >= 24) return '24:00';
  const totalMins = Math.round(h * 60);
  const hh = Math.floor(totalMins / 60) % 24;
  const mm = totalMins % 60;
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

export function formatBlockTimeRange(hourStart: number, hourEnd: number): string {
  return `${formatHourFraction(hourStart)} – ${formatHourFraction(hourEnd)}`;
}

function dateFmt(timeZone: string, opts: Intl.DateTimeFormatOptions) {
  return new Intl.DateTimeFormat('de-DE', { ...opts, timeZone });
}

function formatClock(d: Date, timeZone: string): string {
  return formatWallClockHm(d, timeZone);
}

function isSameWallClockDay(a: Date, b: Date, timeZone: string): boolean {
  const wa = wallClockInTimeZone(a, timeZone);
  const wb = wallClockInTimeZone(b, timeZone);
  return wa.y === wb.y && wa.mo === wb.mo && wa.d === wb.d;
}

/** Datumszeile für Abwesenheitsbalken (ohne Uhrzeit). */
export function formatBlockAbsenceDateLabel(
  viewportStart: Date,
  span: Pick<GridBlock, 'dayStart' | 'dayEnd' | 'scheduleDueAt' | 'scheduleEndAt'>,
  timeZone: string = DEFAULT_ACTIVITY_TIMEZONE,
): string {
  const shortDateFmt = dateFmt(timeZone, { day: 'numeric', month: 'short' });
  const fullDateFmt = dateFmt(timeZone, { day: 'numeric', month: 'short', year: 'numeric' });
  const start = span.scheduleDueAt
    ? new Date(span.scheduleDueAt)
    : addDays(viewportStart, span.dayStart);
  const end = span.scheduleEndAt
    ? new Date(span.scheduleEndAt)
    : addDays(viewportStart, span.dayEnd);
  if (isSameWallClockDay(start, end, timeZone)) {
    return fullDateFmt.format(start);
  }
  const ws = wallClockInTimeZone(start, timeZone);
  const we = wallClockInTimeZone(end, timeZone);
  if (ws.y === we.y) {
    return `${shortDateFmt.format(start)} – ${fullDateFmt.format(end)}`;
  }
  return `${fullDateFmt.format(start)} – ${fullDateFmt.format(end)}`;
}

/** Von–bis mit Datum, wenn Start und Ende an verschiedenen Tagen liegen. */
export function formatBlockScheduleLabel(
  viewportStart: Date,
  span: Pick<
    GridBlock,
    'dayStart' | 'dayEnd' | 'hourStart' | 'hourEnd' | 'scheduleDueAt' | 'scheduleEndAt'
  >,
  timeZone: string = DEFAULT_ACTIVITY_TIMEZONE,
): string {
  let start: Date;
  let end: Date;
  if (span.scheduleDueAt && span.scheduleEndAt) {
    start = new Date(span.scheduleDueAt);
    end = new Date(span.scheduleEndAt);
  } else {
    const iso = spanToIsoRange(viewportStart, span, timeZone);
    start = new Date(iso.due_at);
    end = new Date(iso.end_at);
  }

  if (isSameWallClockDay(start, end, timeZone)) {
    return `${formatClock(start, timeZone)} – ${formatClock(end, timeZone)}`;
  }
  const shortDateFmt = dateFmt(timeZone, { day: 'numeric', month: 'short' });
  const d1 = shortDateFmt.format(start);
  const d2 = shortDateFmt.format(end);
  return `${d1} ${formatClock(start, timeZone)} – ${d2} ${formatClock(end, timeZone)}`;
}

export function snapHour(
  h: number,
  dayHours: PlantafelDayHours = DEFAULT_PLANTAFEL_DISPLAY_SETTINGS.dayHours,
): number {
  const snapped = Math.round(h / SNAP_HOURS) * SNAP_HOURS;
  return Math.max(dayHours.hourStart, Math.min(dayHours.hourEnd, snapped));
}

export function clientXToPct(clientX: number, rect: DOMRect): number {
  return Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
}

export function blockSpanToPct(
  block: Pick<GridBlock, 'dayStart' | 'dayEnd' | 'hourStart' | 'hourEnd'>,
  viewMode: 'tag' | 'woche',
  visibleWeekdays?: PlantafelVisibleWeekdays,
  dayHours: PlantafelDayHours = DEFAULT_PLANTAFEL_DISPLAY_SETTINGS.dayHours,
): { leftPct: number; widthPct: number } {
  const visible = visibleWeekdays?.length ? visibleWeekdays : [0, 1, 2, 3, 4, 5, 6];
  const dayWidth = viewMode === 'tag' ? 100 : weekDayWidthPct(visible);
  const hourSpan = plantafelHourSpan(dayHours);
  const { visStart: hs, visEnd: he } = clipBlockHoursToViewport(
    block.hourStart,
    block.hourEnd,
    dayHours,
  );
  if (viewMode === 'tag') {
    const leftPct = (block.dayStart + (hs - dayHours.hourStart) / hourSpan) * dayWidth;
    const rightPct = (block.dayEnd + (he - dayHours.hourStart) / hourSpan) * dayWidth;
    return {
      leftPct,
      widthPct: Math.max(rightPct - leftPct, (SNAP_HOURS / hourSpan) * dayWidth),
    };
  }
  const span = clipSpanToVisibleColumns(block.dayStart, block.dayEnd, visible);
  if (!span) return { leftPct: 0, widthPct: weekDayWidthPct(visible) };
  const leftPct = span.colStart * dayWidth + ((hs - dayHours.hourStart) / hourSpan) * dayWidth;
  const rightPct = span.colEnd * dayWidth + ((he - dayHours.hourStart) / hourSpan) * dayWidth;
  return {
    leftPct,
    widthPct: Math.max(rightPct - leftPct, (SNAP_HOURS / hourSpan) * dayWidth),
  };
}

export function pctSpanToBlockHours(
  leftPct: number,
  widthPct: number,
  viewMode: 'tag' | 'woche' | 'monat',
  visibleWeekdays?: PlantafelVisibleWeekdays,
  dayHours: PlantafelDayHours = DEFAULT_PLANTAFEL_DISPLAY_SETTINGS.dayHours,
  monthDayCount?: number,
): Pick<GridBlock, 'dayStart' | 'dayEnd' | 'hourStart' | 'hourEnd'> {
  const visible = visibleWeekdays?.length ? visibleWeekdays : [0, 1, 2, 3, 4, 5, 6];
  const monthCols =
    viewMode === 'monat'
      ? visibleColumnCount(visible, Math.max(1, Math.ceil((monthDayCount ?? 35) / 7)))
      : 0;
  const dayWidth =
    viewMode === 'tag' ? 100 : viewMode === 'monat' ? 100 / Math.max(monthCols, 1) : weekDayWidthPct(visible);
  const hourSpan = plantafelHourSpan(dayHours);
  const minWidthPct = (SNAP_HOURS / hourSpan) * dayWidth;
  const maxAbsoluteDay = viewMode === 'monat' ? Math.max(0, (monthDayCount ?? 35) - 1) : 6;

  let l = Math.max(0, leftPct);
  let w = Math.max(minWidthPct, widthPct);
  if (l + w > 100) w = 100 - l;

  const rightPct = l + w;
  const leftColFloat = l / dayWidth;
  const rightColFloat = rightPct / dayWidth;

  let dayStart: number;
  let dayEnd: number;
  let hourStart: number;
  let hourEnd: number;

  if (viewMode === 'tag') {
    const startColFloor = Math.floor(leftColFloat);
    let endColFloor = Math.floor(rightColFloat);
    hourStart = dayHours.hourStart + (leftColFloat - startColFloor) * hourSpan;
    hourEnd = dayHours.hourStart + (rightColFloat - endColFloor) * hourSpan;
    // A right edge exactly on a day boundary represents the end of the previous
    // day, not the start of the next one.
    if (hourEnd <= dayHours.hourStart + 0.01 && endColFloor > startColFloor) {
      endColFloor -= 1;
      hourEnd = dayHours.hourEnd;
    }
    dayStart = 0;
    dayEnd = 0;
  } else {
    const colCount = viewMode === 'monat' ? Math.max(monthCols, 1) : visible.length;
    const startCol = Math.min(Math.floor(leftColFloat), colCount - 1);
    let endCol = Math.floor(rightColFloat);
    hourStart = dayHours.hourStart + (leftColFloat - startCol) * hourSpan;
    hourEnd = dayHours.hourStart + (rightColFloat - endCol) * hourSpan;
    // A right edge that lands exactly on a column boundary (or wraps past the
    // last visible column) represents the end of the previous column, not the
    // start of the next one. Snap it back in *visible-column* space so the
    // block always lands on a visible weekday — operating on the absolute day
    // index here would wrap onto a hidden day (e.g. Sunday) and the event would
    // disappear from the calendar.
    if (endCol > startCol && hourEnd <= dayHours.hourStart + 0.01) {
      endCol -= 1;
      hourEnd = dayHours.hourEnd;
    }
    endCol = Math.max(startCol, Math.min(endCol, colCount - 1));
    dayStart = visibleColumnToAbsoluteDay(startCol, visible);
    dayEnd = visibleColumnToAbsoluteDay(endCol, visible);
  }

  dayStart = Math.max(0, Math.min(maxAbsoluteDay, dayStart));
  dayEnd = Math.max(dayStart, Math.min(maxAbsoluteDay, dayEnd));
  hourStart = snapHour(hourStart, dayHours);
  hourEnd = snapHour(hourEnd, dayHours);

  if (dayStart === dayEnd && hourEnd <= hourStart) {
    hourEnd = Math.min(dayHours.hourEnd, hourStart + SNAP_HOURS);
  }
  hourStart = Math.max(dayHours.hourStart, Math.min(dayHours.hourEnd - SNAP_HOURS, hourStart));
  hourEnd = Math.max(hourStart + SNAP_HOURS, Math.min(dayHours.hourEnd, hourEnd));

  return { dayStart, dayEnd, hourStart, hourEnd };
}

export function spanToIsoRange(
  viewportStart: Date,
  span: Pick<GridBlock, 'dayStart' | 'dayEnd' | 'hourStart' | 'hourEnd'>,
  timeZone: string = DEFAULT_ACTIVITY_TIMEZONE,
): { due_at: string; end_at: string } {
  const startDayKey = localDateKeyFromDate(addDays(viewportStart, span.dayStart), timeZone);
  const endDayKey = localDateKeyFromDate(addDays(viewportStart, span.dayEnd), timeZone);
  const due = localDateTimeFromParts(startDayKey, span.hourStart, timeZone);
  let end: Date;
  if (span.hourEnd >= 24) {
    const nextKey = localDateKeyFromDate(addDays(viewportStart, span.dayEnd + 1), timeZone);
    end = localDateTimeFromParts(nextKey, 0, timeZone);
  } else {
    end = localDateTimeFromParts(endDayKey, span.hourEnd, timeZone);
  }
  return {
    due_at: formatIsoWithLocalOffset(due, timeZone),
    end_at: formatIsoWithLocalOffset(end, timeZone),
  };
}

export function canResizeActivityBlock(
  block: GridBlock,
  viewMode: PlantafelViewMode,
): boolean {
  return !block.isAbsence && !block.isPendingCreate && (viewMode === 'tag' || viewMode === 'woche');
}
